import {
  adminTournamentEntrySchema,
  adminTournamentSchema,
  minForGroups,
  participantTypeSchema,
  tournamentFormatSchema,
  tournamentAudienceSchema,
  type AdminTournament,
  type AdminTournamentEntry,
  type ParticipantType,
  type TournamentAudience,
  type TournamentFormat,
  type TournamentStatus,
} from '@growup/site-shared';
import {
  Button,
  Card,
  Checkbox,
  type Column,
  DateTimeInput,
  Field,
  Input,
  Notice,
  Select,
  StatusBadge,
  type StatusTone,
  Table,
  Textarea,
} from '@growup/ui';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { z } from 'zod';

import { ApiError, getJson, messageOf, send } from '../api';
import { isoToLisbonInput, lisbonInputToIso } from '../events/labels';
import { formatDateTime } from '../format';
import { IMAGE_ASPECTS, ImageField, useImagesBusy } from '../media/ImageUpload';
import { matchesQuery } from '../quickFilter';
import { BracketCard } from './BracketCard';

export const TOURNAMENT_STATUS_LOOK: Record<TournamentStatus, { label: string; tone: StatusTone }> =
  {
    draft: { label: 'Rascunho', tone: 'neutral' },
    published: { label: 'Publicado', tone: 'info' },
    cancelled: { label: 'Cancelado', tone: 'danger' },
  };

export const AUDIENCE_LABEL: Record<TournamentAudience, string> = {
  everyone: 'Todos (qualquer conta)',
  members: 'Membros e associados',
  associates: 'Só associados',
};

export const TYPE_LABEL: Record<ParticipantType, string> = {
  solo: 'Individual',
  team: 'Equipas (só o nome da equipa)',
};

export const FORMAT_LABEL: Record<TournamentFormat, string> = {
  single_elimination: 'Eliminação simples',
  groups_playoffs: 'Fase de grupos + playoffs',
};

const BEST_OF = [1, 3, 5, 7] as const;
const bestOfLabel = (n: number) => (n === 1 ? 'Jogo único' : `Melhor de ${String(n)}`);

const autoSlug = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const COLUMNS: Column<AdminTournament>[] = [
  {
    key: 'title',
    header: 'Torneio',
    cell: (t) => (
      <span className="flex flex-col">
        <span className="font-semibold">{t.title}</span>
        <span className="text-caption text-gu-ink-muted">
          {formatDateTime(t.startsAt)} · {t.game} ·{' '}
          {t.participantType === 'team' ? 'Equipas' : 'Individual'}
        </span>
      </span>
    ),
  },
  {
    key: 'entries',
    header: 'Inscritos',
    align: 'right',
    cell: (t) =>
      `${String(t.confirmed)}/${String(t.maxParticipants)}${t.waitlisted > 0 ? ` +${String(t.waitlisted)} em espera` : ''}`,
  },
  {
    key: 'status',
    header: 'Estado',
    align: 'right',
    cell: (t) => <StatusBadge tone={statusOf(t).tone}>{statusOf(t).label}</StatusBadge>,
  },
];

function statusOf(t: AdminTournament): { label: string; tone: StatusTone } {
  if (t.status === 'published' && t.finishedAt !== null) {
    return { label: 'Terminado', tone: 'success' };
  }
  if (t.status === 'published' && t.bracketGeneratedAt !== null) {
    return { label: 'A decorrer', tone: 'warning' };
  }
  return TOURNAMENT_STATUS_LOOK[t.status];
}

/** Torneios (docs/torneios): tudo precisa de `tournaments.manage` (Gestor de Torneios). */
export function TournamentsSection() {
  const [list, setList] = useState<AdminTournament[] | null>(null);
  const [selected, setSelected] = useState<string | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    getJson('/api/site/tournaments', z.array(adminTournamentSchema))
      .then(setList)
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);
  useEffect(load, [load]);

  const onSaved = (saved: AdminTournament) => {
    setList((current) =>
      current === null
        ? [saved]
        : current.some((t) => t.id === saved.id)
          ? current.map((t) => (t.id === saved.id ? saved : t))
          : [saved, ...current],
    );
    setSelected(saved.id);
  };
  const tournament = list?.find((t) => t.id === selected) ?? null;

  if (selected === 'new' || tournament) {
    return (
      <div className="flex flex-col gap-4">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSelected(null);
              load();
            }}
          >
            ← Torneios
          </Button>
        </div>
        {tournament ? (
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-display text-title uppercase">{tournament.title}</h2>
            <StatusBadge tone={statusOf(tournament).tone}>{statusOf(tournament).label}</StatusBadge>
          </div>
        ) : null}
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <TournamentForm key={tournament?.id ?? 'new'} tournament={tournament} onSaved={onSaved} />
          {tournament ? <PublishPanel tournament={tournament} onSaved={onSaved} /> : null}
        </div>
        {tournament && tournament.status === 'published' ? (
          <BracketCard tournament={tournament} onChanged={load} />
        ) : null}
        {tournament ? <EntriesCard tournament={tournament} onChanged={load} /> : null}
      </div>
    );
  }

  return (
    <Card
      headingLevel={2}
      title="Torneios"
      actions={
        <Button size="sm" onClick={() => setSelected('new')}>
          Novo torneio
        </Button>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <Table
        caption="Torneios"
        columns={COLUMNS}
        rows={list ?? []}
        rowKey={(t) => t.id}
        empty={list ? 'Ainda não há torneios.' : 'A carregar…'}
        onRowClick={(t) => setSelected(t.id)}
      />
    </Card>
  );
}

function TournamentForm({
  tournament,
  onSaved,
}: {
  tournament: AdminTournament | null;
  onSaved: (t: AdminTournament) => void;
}) {
  const t = tournament;
  const [title, setTitle] = useState(t?.title ?? '');
  const [slug, setSlug] = useState(t?.slug ?? '');
  const [game, setGame] = useState(t?.game ?? '');
  const [summary, setSummary] = useState(t?.summary ?? '');
  const [description, setDescription] = useState(t?.description ?? '');
  const [rules, setRules] = useState(t?.rules ?? '');
  const [imageUrl, setImageUrl] = useState<string | null>(t?.imageUrl ?? null);
  const [type, setType] = useState<ParticipantType>(t?.participantType ?? 'solo');
  const [audience, setAudience] = useState<TournamentAudience>(t?.audience ?? 'everyone');
  const [max, setMax] = useState(String(t?.maxParticipants ?? 16));
  const [online, setOnline] = useState(t?.online ?? false);
  const [venue, setVenue] = useState(t?.venue ?? '');
  const [startsAt, setStartsAt] = useState(isoToLisbonInput(t?.startsAt ?? null));
  const [opensAt, setOpensAt] = useState(isoToLisbonInput(t?.registrationOpensAt ?? null));
  const [closesAt, setClosesAt] = useState(isoToLisbonInput(t?.registrationClosesAt ?? null));
  const [format, setFormat] = useState<TournamentFormat>(t?.format ?? 'single_elimination');
  const [groupCount, setGroupCount] = useState(String(t?.groupCount ?? 2));
  const [advance, setAdvance] = useState(String(t?.advancePerGroup ?? 2));
  const [thirdPlace, setThirdPlace] = useState(t?.thirdPlace ?? false);
  const [checkIn, setCheckIn] = useState(t ? t.checkInMinutes !== null : false);
  const [checkInMinutes, setCheckInMinutes] = useState(String(t?.checkInMinutes ?? 30));
  const [bestOf, setBestOf] = useState(String(t?.bestOf ?? 1));
  const [finalBestOf, setFinalBestOf] = useState(
    t?.finalBestOf == null ? '' : String(t.finalBestOf),
  );
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const photos = useImagesBusy();
  const locked = t?.status === 'cancelled';
  const published = t?.status === 'published';
  const started = t?.bracketGeneratedAt != null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const problems: Record<string, string> = {};
    const starts = lisbonInputToIso(startsAt);
    if (starts === null) problems.startsAt = 'Indique a data e a hora.';
    if (!/^\d+$/.test(max.trim()) || Number(max) < 2 || Number(max) > 128) {
      problems.maxParticipants = 'Um número inteiro de 2 a 128.';
    } else if (
      format === 'groups_playoffs' &&
      Number(max) < minForGroups(Number(groupCount), Number(advance))
    ) {
      problems.maxParticipants = `Com estes grupos, pelo menos ${String(minForGroups(Number(groupCount), Number(advance)))} lugares.`;
    }
    if (
      checkIn &&
      (!/^\d+$/.test(checkInMinutes.trim()) ||
        Number(checkInMinutes) < 5 ||
        Number(checkInMinutes) > 240)
    ) {
      problems.checkInMinutes = 'De 5 a 240 minutos.';
    }
    setFields(problems);
    if (Object.keys(problems).length > 0) return;
    const body = {
      slug,
      title,
      game,
      summary,
      description,
      rules,
      imageUrl,
      participantType: type,
      audience,
      maxParticipants: Number(max),
      online,
      venue,
      startsAt: starts,
      registrationOpensAt: lisbonInputToIso(opensAt),
      registrationClosesAt: lisbonInputToIso(closesAt),
      format,
      groupCount: Number(groupCount),
      advancePerGroup: Number(advance),
      thirdPlace,
      checkInMinutes: checkIn ? Number(checkInMinutes) : null,
      bestOf: Number(bestOf),
      finalBestOf: finalBestOf === '' ? null : Number(finalBestOf),
    };
    setBusy(true);
    setError(null);
    setSaved(null);
    try {
      const result = t
        ? await send('PUT', `/api/site/tournaments/${t.id}`, body)
        : await send('POST', '/api/site/tournaments', body);
      onSaved(adminTournamentSchema.parse(result));
      setSaved(t ? 'Guardado.' : 'Criado como rascunho. Publique quando estiver pronto.');
    } catch (err) {
      setError(messageOf(err));
      if (err instanceof ApiError) setFields(err.fields);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card headingLevel={2} title={t ? 'Dados do torneio' : 'Novo torneio'}>
      <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)} noValidate>
        <fieldset disabled={locked} className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Nome do torneio" error={fields.title}>
              <Input
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (!t) setSlug(autoSlug(e.target.value));
                }}
              />
            </Field>
            <Field label="Endereço" hint={`growup.gg/torneios/${slug || '…'}`} error={fields.slug}>
              <Input value={slug} onChange={(e) => setSlug(e.target.value)} />
            </Field>
            <Field label="Jogo" hint="Ex.: EA FC 26, League of Legends." error={fields.game}>
              <Input value={game} onChange={(e) => setGame(e.target.value)} />
            </Field>
            <Field
              label="Máximo de participantes"
              hint="Os seguintes ficam em lista de espera."
              error={fields.maxParticipants}
            >
              <Input inputMode="numeric" value={max} onChange={(e) => setMax(e.target.value)} />
            </Field>
            <Field
              label="Tipo"
              hint="Depois de publicar já não muda."
              error={fields.participantType}
            >
              <Select
                value={type}
                disabled={published}
                onChange={(e) => setType(participantTypeSchema.parse(e.target.value))}
              >
                {participantTypeSchema.options.map((o) => (
                  <option key={o} value={o}>
                    {TYPE_LABEL[o]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Quem se pode inscrever"
              hint="Depois de publicar já não muda. Os jovens entram pelo encarregado."
              error={fields.audience}
            >
              <Select
                value={audience}
                disabled={published}
                onChange={(e) => setAudience(tournamentAudienceSchema.parse(e.target.value))}
              >
                {tournamentAudienceSchema.options.map((a) => (
                  <option key={a} value={a}>
                    {AUDIENCE_LABEL[a]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Começa em" hint="Hora de Lisboa." error={fields.startsAt}>
              <DateTimeInput value={startsAt} onChange={setStartsAt} />
            </Field>
            <Field
              label={online ? 'Plataforma' : 'Local'}
              optional
              hint={online ? 'Ex.: Discord da Grow uP, PlayStation Network.' : 'Ex.: Arena Norte.'}
              error={fields.venue}
            >
              <Input value={venue} onChange={(e) => setVenue(e.target.value)} />
            </Field>
            <div className="sm:col-span-2">
              <Checkbox
                label="Torneio online"
                checked={online}
                onChange={(e) => setOnline(e.target.checked)}
              />
            </div>
            <Field
              label="Inscrições abrem em"
              optional
              hint="Vazio = ao publicar."
              error={fields.registrationOpensAt}
            >
              <DateTimeInput value={opensAt} onChange={setOpensAt} />
            </Field>
            <Field
              label="Inscrições fecham em"
              optional
              hint="Vazio = quando o torneio começa."
              error={fields.registrationClosesAt}
            >
              <DateTimeInput value={closesAt} onChange={setClosesAt} />
            </Field>
            <fieldset
              className="flex flex-col gap-3 border-t border-gu-line pt-4 sm:col-span-2"
              disabled={started}
            >
              <legend className="font-sub text-label font-bold uppercase">Formato</legend>
              {started ? (
                <p className="text-caption text-gu-ink-muted">
                  O bracket já foi gerado: para mudar isto, refaça o bracket.
                </p>
              ) : null}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Field label="Formato" error={fields.format}>
                  <Select
                    value={format}
                    onChange={(e) => setFormat(tournamentFormatSchema.parse(e.target.value))}
                  >
                    {tournamentFormatSchema.options.map((f) => (
                      <option key={f} value={f}>
                        {FORMAT_LABEL[f]}
                      </option>
                    ))}
                  </Select>
                </Field>
                {format === 'groups_playoffs' ? (
                  <>
                    <Field label="Grupos" error={fields.groupCount}>
                      <Select value={groupCount} onChange={(e) => setGroupCount(e.target.value)}>
                        {[2, 4, 8].map((n) => (
                          <option key={n} value={String(n)}>
                            {n} grupos
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field label="Passam aos playoffs" error={fields.advancePerGroup}>
                      <Select value={advance} onChange={(e) => setAdvance(e.target.value)}>
                        <option value="1">O 1.º de cada grupo</option>
                        <option value="2">Os 2 primeiros de cada grupo</option>
                      </Select>
                    </Field>
                  </>
                ) : null}
              </div>
              {format === 'groups_playoffs' ? (
                <p className="text-caption text-gu-ink-muted">
                  Nos grupos joga cada um contra todos: vitória 3 pontos, empate 1 (só em jogo
                  único), derrota 0. Desempate: confronto direto, diferença, resultado marcado e
                  cabeça de série. Nos playoffs, o 1.º de um grupo joga com o 2.º do outro.
                </p>
              ) : null}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Cada jogo é" error={fields.bestOf}>
                  <Select value={bestOf} onChange={(e) => setBestOf(e.target.value)}>
                    {BEST_OF.map((n) => (
                      <option key={n} value={String(n)}>
                        {bestOfLabel(n)}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="A final é" error={fields.finalBestOf}>
                  <Select value={finalBestOf} onChange={(e) => setFinalBestOf(e.target.value)}>
                    <option value="">Igual aos outros jogos</option>
                    {BEST_OF.map((n) => (
                      <option key={n} value={String(n)}>
                        {bestOfLabel(n)}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>
              <Checkbox
                label="Jogo de 3.º e 4.º lugar (entre os vencidos das meias-finais)"
                checked={thirdPlace}
                onChange={(e) => setThirdPlace(e.target.checked)}
              />
              <Checkbox
                label="Check-in antes de começar"
                checked={checkIn}
                onChange={(e) => setCheckIn(e.target.checked)}
              />
              {checkIn ? (
                <Field
                  label="Minutos antes do início"
                  hint="O check-in abre aqui e fecha quando gerar o bracket. Só joga quem o fizer; os lugares de quem faltar vão para a lista de espera."
                  error={fields.checkInMinutes}
                >
                  <Input
                    inputMode="numeric"
                    className="max-w-[8rem]"
                    value={checkInMinutes}
                    onChange={(e) => setCheckInMinutes(e.target.value)}
                  />
                </Field>
              ) : null}
            </fieldset>
            <ImageField
              label="Imagem"
              optional
              value={imageUrl}
              onChange={setImageUrl}
              aspect={IMAGE_ASPECTS.tournament}
              onBusyChange={photos.track('imagem')}
              error={fields.imageUrl}
            />
          </div>
          <Field label="Resumo" optional error={fields.summary}>
            <Input value={summary} onChange={(e) => setSummary(e.target.value)} />
          </Field>
          <Field
            label="Descrição"
            optional
            hint="Parágrafos separados por uma linha em branco."
            error={fields.description}
          >
            <Textarea
              rows={6}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
          <Field
            label="Regras"
            optional
            hint="Aparecem na página do torneio, numa secção que abre."
            error={fields.rules}
          >
            <Textarea rows={8} value={rules} onChange={(e) => setRules(e.target.value)} />
          </Field>
        </fieldset>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {saved ? <Notice tone="success">{saved}</Notice> : null}
        {!locked ? (
          <div>
            <Button type="submit" disabled={busy || photos.busy}>
              {t ? 'Guardar' : 'Criar rascunho'}
            </Button>
          </div>
        ) : null}
      </form>
    </Card>
  );
}

/** Publicar ou cancelar (com aviso por email a quem estava inscrito). */
function PublishPanel({
  tournament,
  onSaved,
}: {
  tournament: AdminTournament;
  onSaved: (t: AdminTournament) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(false);
  const base = `/api/site/tournaments/${tournament.id}`;

  const run = async (path: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      onSaved(adminTournamentSchema.parse(await send('POST', `${base}${path}`, body)));
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card headingLevel={2} title="Publicação">
      <div className="flex flex-col gap-3">
        {tournament.status === 'draft' ? (
          <>
            <p className="text-gu-ink-muted">
              Rascunho: ainda não aparece no site. Ao publicar, fica em growup.gg/torneios e as
              inscrições abrem (ou abrem na data marcada).
            </p>
            <Button disabled={busy} onClick={() => void run('/publish')}>
              Publicar
            </Button>
          </>
        ) : null}
        {tournament.status === 'published' ? (
          <p className="text-gu-ink-muted">
            {tournament.window === 'open'
              ? 'Inscrições abertas.'
              : tournament.window === 'not_yet'
                ? 'As inscrições ainda não abriram.'
                : 'Inscrições fechadas.'}{' '}
            {tournament.confirmed} de {tournament.maxParticipants} lugares ocupados
            {tournament.waitlisted > 0 ? `, ${String(tournament.waitlisted)} em espera` : ''}.
          </p>
        ) : null}
        {tournament.status === 'cancelled' ? (
          <Notice tone="danger" title="Cancelado">
            {tournament.cancelReason ?? ''}
          </Notice>
        ) : (
          <div className="flex flex-col gap-2 border-t border-gu-line pt-3">
            <Field label="Motivo do cancelamento">
              <Input value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
            <Checkbox
              label={
                tournament.status === 'published'
                  ? 'Confirmo: cancelar e avisar os inscritos por email'
                  : 'Confirmo: cancelar o torneio'
              }
              checked={confirmCancel}
              onChange={(e) => setConfirmCancel(e.target.checked)}
            />
            <Button
              variant="secondary"
              disabled={busy || !confirmCancel || reason.trim() === ''}
              onClick={() => void run('/cancel', { reason: reason.trim() })}
            >
              Cancelar torneio
            </Button>
          </div>
        )}
        {error ? <Notice tone="danger">{error}</Notice> : null}
      </div>
    </Card>
  );
}

function EntriesCard({
  tournament,
  onChanged,
}: {
  tournament: AdminTournament;
  onChanged: () => void;
}) {
  const [entries, setEntries] = useState<AdminTournamentEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const base = `/api/site/tournaments/${tournament.id}/entries`;
  const team = tournament.participantType === 'team';
  const started = tournament.bracketGeneratedAt !== null;
  const checkInOpen = tournament.checkIn !== null && tournament.checkIn !== 'closed';

  useEffect(() => {
    getJson(base, z.array(adminTournamentEntrySchema))
      .then(setEntries)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [base, tournament.confirmed, tournament.waitlisted, tournament.bracketGeneratedAt]);

  const change = async (method: 'POST' | 'DELETE', path: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      setEntries(z.array(adminTournamentEntrySchema).parse(await send(method, path, body)));
      onChanged();
      return true;
    } catch (e) {
      setError(messageOf(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const columns: Column<AdminTournamentEntry>[] = [
    {
      key: 'name',
      header: team ? 'Equipa' : 'Participante',
      cell: (e) => (
        <span className="flex flex-col">
          <span className="font-semibold">{e.name}</span>
          <span className="text-caption text-gu-ink-muted">
            {e.accountLogin === null
              ? `Juntado pelo staff (${e.enteredByLogin})`
              : e.enteredByLogin !== e.accountLogin
                ? `${team ? `Conta ${e.accountLogin} · ` : ''}Inscrito por ${e.enteredByLogin}`
                : team
                  ? `Conta ${e.accountLogin}`
                  : ''}
          </span>
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Estado',
      cell: (e) =>
        e.status === 'confirmed' ? (
          <StatusBadge tone="success">Confirmado</StatusBadge>
        ) : (
          <StatusBadge tone="warning">Em espera</StatusBadge>
        ),
    },
    ...(tournament.checkInMinutes !== null
      ? [
          {
            key: 'checkin',
            header: 'Check-in',
            cell: (e: AdminTournamentEntry) =>
              checkInOpen ? (
                <Button
                  size="sm"
                  variant={e.checkedIn ? 'secondary' : 'ghost'}
                  disabled={busy}
                  aria-pressed={e.checkedIn}
                  aria-label={`Check-in de ${e.name}`}
                  onClick={() =>
                    void change('POST', `${base}/${e.id}/check-in`, { checkedIn: !e.checkedIn })
                  }
                >
                  {e.checkedIn ? '✓ Feito' : 'Marcar'}
                </Button>
              ) : e.checkedIn ? (
                '✓'
              ) : (
                '—'
              ),
          },
        ]
      : []),
    ...(started
      ? [
          {
            key: 'seed',
            header: 'Cabeça de série',
            align: 'right' as const,
            cell: (e: AdminTournamentEntry) => (e.seed === null ? 'Não joga' : String(e.seed)),
          },
        ]
      : []),
    {
      key: 'when',
      header: 'Inscrito em',
      align: 'right',
      cell: (e) => formatDateTime(e.createdAt),
    },
    {
      key: 'remove',
      header: '',
      align: 'right',
      cell: (e) =>
        !started ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => {
              if (window.confirm(`Tirar ${e.name} do torneio?`)) {
                void change('DELETE', `${base}/${e.id}`);
              }
            }}
          >
            Tirar
          </Button>
        ) : e.disqualified ? (
          <StatusBadge tone="neutral">Desistiu: {e.disqualifyReason ?? ''}</StatusBadge>
        ) : e.seed !== null && tournament.finishedAt === null ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => {
              const reason = window.prompt(
                `${e.name} desiste ou é desqualificado? Os jogos dele passam a W.O. Motivo:`,
                'Desistiu',
              );
              if (reason === null || reason.trim() === '') return;
              void change('POST', `${base}/${e.id}/disqualify`, { reason: reason.trim() });
            }}
          >
            Desistiu
          </Button>
        ) : null,
    },
  ];

  const add = async (ev: FormEvent) => {
    ev.preventDefault();
    if (name.trim() === '') return;
    if (await change('POST', base, { name: name.trim() })) setName('');
  };

  return (
    <Card headingLevel={2} title={team ? 'Equipas inscritas' : 'Participantes'}>
      <div className="flex flex-col gap-3">
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {started ? (
          <p className="text-caption text-gu-ink-muted">
            Com o bracket gerado, as inscrições não mudam. Para juntar ou tirar alguém, refaça o
            bracket.
          </p>
        ) : null}
        {tournament.status !== 'cancelled' && !started ? (
          <form className="flex flex-wrap items-end gap-2" onSubmit={(ev) => void add(ev)}>
            <Field
              label={team ? 'Juntar equipa' : 'Juntar participante'}
              hint="Só com o nome, sem conta (ex.: apareceu no dia)."
            >
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Button type="submit" variant="secondary" disabled={busy || name.trim() === ''}>
              Juntar
            </Button>
          </form>
        ) : null}
        {entries && entries.length > 0 ? (
          <Input
            type="search"
            aria-label="Procurar inscrição"
            placeholder="Procurar nome ou login"
            className="max-w-xs"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        ) : null}
        <Table
          caption={team ? 'Equipas inscritas' : 'Participantes'}
          columns={columns}
          rows={(entries ?? []).filter((e) =>
            matchesQuery(query, e.name, e.accountLogin ?? '', e.enteredByLogin),
          )}
          rowKey={(e) => e.id}
          empty={entries ? 'Ainda ninguém se inscreveu.' : 'A carregar…'}
        />
      </div>
    </Card>
  );
}
